import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch } from 'react-hook-form';
import { useSession } from '@/auth/Session';
import { SearchFormPlacements } from '@/components/SearchFormsContainer/interfaces';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { RecentReportsSuggestions } from '@/reports/shared/RecentReportsSuggestions/RecentReportsSuggestions';
import {
  FcraWarning,
  InputLabel,
} from '@/reports/shared/searchFormsComponents/components';
import { useReportRedirect } from '@/reports/useReportRedirect/useReportRedirect';
import { Button, Input, Text, Select, Icons, Stack, Grid } from '@/theme/index';
import { searchFormsRecyclingValidation } from '@/utils/index';
import { schemaContactSearchForm } from '@/utils/schemaForms/schemaForms';
import { useStatesOptionsAbbr } from '@/utils/states';
import { useScreenSize } from '@/utils/useScreenSize';
import { parseFullName } from './utils';

interface ContactInfo {
  /** The name of the contact */
  name: string;
  /** The job title of the contact */
  title: string;
  /** The company where the contact works for */
  company: string;
  /** The city where the contact lives or lived */
  city: string;
  /**
   * The state where the contact lives or lived.
   */
  state: string;
}

export function SearchContactForm({
  closeForm,
  variant,
  placement = 'header',
}: {
  closeForm: () => void;
  variant?: 'footer';
} & SearchFormPlacements) {
  const isConsolidatedPersonSearch = useFeatureIsOn('prop-2547');
  const { session } = useSession();
  const { account } = session;
  const { trackEventGA4 } = useAppConfigContext();
  const { redirectToSearchContactUrl, redirectToSearchPersonUrlv2 } =
    useReportRedirect();
  const states = useStatesOptionsAbbr();
  const {
    links: { tosLink, fcraLink },
    data: { brandName },
  } = useAppConstantsContext();

  const defaultValues = {
    name: '',
    title: '',
    company: '',
    city: '',
    state: '',
  };

  const onSearchContact = ({
    name,
    title,
    company,
    city,
    state,
  }: ContactInfo) => {
    if (!searchFormsRecyclingValidation(account)) return;
    if ([name, title, company, city, state].every((field) => field === '')) {
      return;
    }

    trackEventGA4({
      event_name: 'report_search',
      report_type: 'contact',
      search_by: 'job_title',
      search_from: `${variant === 'footer' ? 'footer' : 'header'}_search`,
    });
    const { fName, mName, lName } = parseFullName(name || '');
    const contactInfo = {
      name,
      fName,
      mName,
      lName,
      fullName: `${name}`,
      title,
      company,
      city,
      state,
    };

    if (isConsolidatedPersonSearch) {
      redirectToSearchPersonUrlv2(contactInfo);
      closeForm();
      return;
    }
    redirectToSearchContactUrl(contactInfo);
    closeForm();
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    control,
    setValue,
  } = useForm<ContactInfo>({
    resolver: yupResolver(schemaContactSearchForm),
    defaultValues: {
      name: defaultValues.name,
      title: defaultValues.title,
      company: defaultValues.company,
      city: defaultValues.city,
      state: defaultValues.state,
    },
  });

  function onStateSelect(value: string) {
    setValue('state', value);
  }

  const inputs = useWatch({
    control,
    name: ['name', 'title', 'company', 'city', 'state'],
  });

  const { isMobile } = useScreenSize();
  const inputsHasValue = inputs.some((input) => input.trim() !== '');
  const gridSpacing = variant === 'footer' ? 0.5 : 2;
  const isInHeader = placement === 'header';
  const isNotInDashboard = placement !== 'dashboard';

  const stringInputRegex = new RegExp('/[^a-zA-ZÀ-ÖØ-öø-ÿ]/g'); // This is a regex to remove space between characters

  return (
    <form
      id="search-contact"
      onSubmit={handleSubmit(onSearchContact)}
      autoComplete="off"
    >
      <Grid
        container
        spacing={gridSpacing}
        px={2}
        alignItems={{ xs: 'center', md: 'flex-end' }}
        flexDirection={{ xs: 'column', md: 'row' }}
        flexWrap="wrap"
      >
        <Grid item width={{ xs: '100%', md: '20%' }} order={1}>
          {isNotInDashboard && <InputLabel htmlFor="name">Name</InputLabel>}
          <Input
            {...register('name')}
            fullWidth
            id="name"
            type="text"
            name="name"
            placeholder="First and Last Name"
            error={Boolean(errors.name)}
            value={inputs[0].replace(stringInputRegex, defaultValues.name)}
          />
        </Grid>
        <Grid item width={{ xs: '100%', md: '18%' }} order={2}>
          {isNotInDashboard && (
            <InputLabel htmlFor="title">Job Title</InputLabel>
          )}
          <Input
            {...register('title')}
            fullWidth
            id="title"
            type="text"
            name="title"
            placeholder="Job Title"
            value={inputs[1].replace(stringInputRegex, defaultValues.title)}
          />
        </Grid>
        <Grid item width={{ xs: '100%', md: '20%' }} order={3}>
          {isNotInDashboard && (
            <InputLabel htmlFor="company">Company</InputLabel>
          )}
          <Input
            {...register('company')}
            fullWidth
            id="company"
            type="text"
            name="company"
            placeholder="Company"
            value={inputs[2].replace(stringInputRegex, defaultValues.company)}
          />
        </Grid>

        {!isMobile && (
          <>
            <Grid item width={{ md: '18%' }} order={4}>
              {isNotInDashboard && <InputLabel htmlFor="city">City</InputLabel>}
              <Input
                {...register('city')}
                fullWidth
                id="city"
                type="text"
                name="city"
                placeholder="City"
                value={inputs[3].replace(stringInputRegex, defaultValues.city)}
              />
            </Grid>
            <Grid item width={{ md: '10%' }} order={5}>
              {isNotInDashboard && (
                <InputLabel htmlFor="state">State</InputLabel>
              )}
              <Select
                {...register('state')}
                options={states}
                onOptionSelect={onStateSelect}
                id="state"
                value={inputs[4]}
                error={Boolean(errors.state)}
              />
            </Grid>
          </>
        )}
        <Grid item width={{ xs: '100%', md: '14%' }} order={{ xs: 6, md: 5 }}>
          <Button type="submit" variant="contained" color="success" fullWidth>
            Search
          </Button>
        </Grid>
        {inputsHasValue && (
          <Grid item width="100%" order={{ xs: 7, md: 8 }}>
            <Button onClick={() => reset()} color="success">
              Clear field(s)
            </Button>
          </Grid>
        )}
      </Grid>
      <Stack py={1} px={2}>
        {(errors.name || errors.state) && (
          <Text color="error">
            <Icons.Warning
              fontSize="small"
              sx={{ verticalAlign: 'bottom', mr: 0.5 }}
            />
            {errors.name?.message || errors.state?.message}
          </Text>
        )}
        <Text variant="body2" marginBottom={1}>
          <Icons.Lightbulb
            fontSize="small"
            color="primary"
            sx={{ verticalAlign: 'bottom', mr: 0.5 }}
          />
          More filters are available after searching
        </Text>
        {isInHeader && (
          <RecentReportsSuggestions
            reportType="detailed_person_report"
            closeForm={closeForm}
          />
        )}
        <FcraWarning
          fcraLink={fcraLink}
          tosLink={tosLink}
          brandName={brandName}
        />
      </Stack>
    </form>
  );
}
